// src/config/useCasesConfig.js

import { BarChart3 , Phone, HardHat, ShieldCheck, FileText, 
  QrCode,  Scissors,
  CircleDot,
  Circle,
  Activity,
  Ruler,
  MoveHorizontal,
  MoveVertical,
  Gauge, Package,
  Boxes,
  Truck,
  CheckCircle2,} from "lucide-react";
import { MdFactCheck, MdCategory, MdHealthAndSafety, MdLocalParking,
  MdDirectionsCar, MdWarning, MdAccessTime } from "react-icons/md";
import { FaHardHat } from "react-icons/fa";

const useCasesConfig = {

  /* =========================
     QSR / FAST FOOD
  ========================== */
  "QSR / Fast Food": {
    "Restaurant Operations": [
      {
        id: "canteen-monitoring",
        title: "Canteen Monitoring",
        icon: BarChart3,
        functionalities: [
          { name: "Table occupancy detection", isPremium: false },
          { name: "Real-time person count in canteen", isPremium: false },
          { name: "Staff mobile phone usage detection", isPremium: false }
        ]
      },
      {
        id: "safety-monitoring",
        title: "Safety Monitoring",
        icon: MdHealthAndSafety,
        functionalities: [
          { name: "Fall Detection", isPremium: true },
          { name: "Fight Detection", isPremium: true }
        ]
      },
      {
        id: "employee-productivity",
        title: "Employee Productivity",
        icon: BarChart3,
        functionalities: [
          { name: "Staff idle time detection", isPremium: true },
          { name: "Speed of service monitoring", isPremium: true },
          { name: "Task completion tracking", isPremium: true }
        ]
      },
      {
        id: "food-safety-hygiene",
        title: "Food Safety & Hygiene",
        icon: MdFactCheck,
        functionalities: [
          { name: "Improper food handling detection", isPremium: false },
          { name: "Surface cleanliness monitoring", isPremium: false },
          { name: "Food left uncovered detection", isPremium: false }
        ]
      },
      {
        id: "customer-wait-times",
        title: "Customer Wait Times",
        icon: BarChart3,
        functionalities: [
          { name: "Queue length detection", isPremium: false },
          { name: "Order to fulfillment time tracking", isPremium: true },
          { name: "POS bottleneck monitoring", isPremium: true }
        ]
      },
      {
        id: "staff-ppe-compliance",
        title: "Staff PPE Compliance",
        icon: FaHardHat,
        functionalities: [
          { name: "Gloves detection", isPremium: false },
          { name: "Hairnet detection", isPremium: false },
          { name: "Apron compliance monitoring", isPremium: false }
        ]
      },
      {
        id: "inventory-wastage",
        title: "Inventory & Wastage",
        icon: MdCategory,
        functionalities: [
          { name: "Portion size monitoring", isPremium: false },
          { name: "Food disposal frequency tracking", isPremium: true },
          { name: "Stock movement monitoring", isPremium: true }
        ]
      },
      {
        id: "shift-scheduling",
        title: "Shift Scheduling Insight",
        icon: BarChart3,
        functionalities: [
          { name: "Peak hours analysis", isPremium: true },
          { name: "Understaffed period detection", isPremium: true },
          { name: "Zone level busyness analysis", isPremium: true }
        ]
      },
      {
        id: "cash-pos-reconciliation",
        title: "Cash & POS Reconciliation",
        icon: MdFactCheck,
        functionalities: [
          { name: "Till activity monitoring", isPremium: false },
          { name: "Frequent void detection", isPremium: true },
          { name: "Cash handling anomaly detection", isPremium: true }
        ]
      }
    ]
  },

  /* =========================
     MANUFACTURING
  ========================== */
  Manufacturing: {
    "Factory Monitoring": [
      {
        id: "ppe-compliance",
        title: "PPE Compliance", // ✅ ADD
        icon: FaHardHat,         // ✅ ADD
        functionalities: [
          { name: "Hard hat detection", isPremium: false },
          { name: "Safety glasses detection", isPremium: false },
          { name: "Gloves detection", isPremium: false }
        ]
      },
      {
        id: "line-productivity",
        title: "Line Productivity Monitoring",
        icon: BarChart3,
        functionalities: [
          { name: "Machine idle time detection", isPremium: true },
          { name: "Operator downtime monitoring", isPremium: true },
          { name: "Cycle time tracking", isPremium: true }
        ]
      },
      {
        id: "quality-defect",
        title: "Quality Defect Detection",
        icon: MdFactCheck,
        functionalities: [
          { name: "Product defect detection", isPremium: false },
          { name: "Packaging defect detection", isPremium: false }
        ]
      },
      {
        id: "unauthorised-zone",
        title: "Unauthorised Zone Access",
        icon: MdCategory,
        functionalities: [
          { name: "Restricted zone entry detection", isPremium: true },
          { name: "Hazardous area monitoring", isPremium: true }
        ]
      },
      {
        id: "equipment-anomaly",
        title: "Equipment Anomaly Detection",
        icon: MdFactCheck,
        functionalities: [
          { name: "Smoke detection near machines", isPremium: false },
          { name: "Spark detection", isPremium: false },
          { name: "Abnormal machine activity detection", isPremium: true }
        ]
      },
      {
        id: "ergonomics-fatigue",
        title: "Ergonomics & Fatigue",
        icon: Activity,
        functionalities: [
          { name: "Worker fatigue detection", isPremium: true },
          { name: "Unsafe posture detection", isPremium: true }
        ]
      },
      {
        id: "visitor-management",
        title: "Visitor & Contractor Management",
        icon: BarChart3,
        functionalities: [
          { name: "Visitor presence detection", isPremium: false },
          { name: "Unauthorized contractor detection", isPremium: true }
        ]
      },
      {
        id: "safety-monitoring",
        title: "Safety Monitoring",
        icon: MdHealthAndSafety,
        functionalities: [
          { name: "Fall Detection", isPremium: true },
          { name: "Fight Detection", isPremium: true }
        ]
      }
    ]
  },

  /* =========================
     HEALTHCARE
  ========================== */
  Healthcare: {
    "Hospital Monitoring": [
      {
        id: "hand-hygiene",
        title: "Hand Hygiene Compliance",
        icon: MdFactCheck,
        functionalities: [
          "Hand sanitisation detection",
          "Wash station monitoring"
        ]
      },
      {
        id: "patient-fall",
        title: "Patient Fall Detection",
        icon: MdFactCheck,
        functionalities: [
          "Patient fall detection",
          "Patient leaving bed detection"
        ]
      },
      {
        id: "ppe-gowning",
        title: "PPE & Gowning Compliance",
        icon: FaHardHat,
        functionalities: [
          "Mask detection",
          "Glove detection",
          "Gown compliance"
        ]
      },
      {
        id: "restricted-area",
        title: "Restricted Area Monitoring",
        icon: MdCategory,
        functionalities: [
          "Medication room monitoring",
          "ICU restricted access detection",
        ]
      },
      {
        id: "safety-monitoring",
        title: "Safety Monitoring",
        icon: MdHealthAndSafety,
        functionalities: [
          "Fall Detection",
          "Fight Detection"
        ]
      },
      {
        id: "waiting-room",
        title: "Waiting Room Analytics",
        icon: BarChart3,
        functionalities: [
          "Queue monitoring",
          "Patient wait time detection"
        ]
      },
      {
        id: "asset-tracking",
        title: "Asset Tracking",
        icon: BarChart3,
        functionalities: [
          "Wheelchair tracking",
          "IV pole tracking",
          "Crash cart tracking"
        ]
      },
      {
        id: "duress-detection",
        title: "Duress & Aggression Detection",
        icon: MdCategory,
        functionalities: [
          "Aggressive posture detection",
          "Confrontation detection"
        ]
      }
    ]
  },

  /* =========================
     RETAIL
  ========================== */
  Retail: {
    "Retail Analytics": [
      {
        id: "footfall-heatmap",
        title: "Footfall & Heatmapping",
        icon: BarChart3,
        functionalities: [
          { name: "Customer entry/exit counting", isPremium: false },
          { name: "High traffic zone detection", isPremium: true },
          { name: "Dwell time analysis", isPremium: true }
        ]
      },
      {
        id: "queue-management",
        title: "Queue Management",
        icon: MdFactCheck,
        functionalities: [
          { name: "Checkout queue monitoring", isPremium: false },
          { name: "Service time tracking", isPremium: true }
        ]
      },
      {
        id: "theft-prevention",
        title: "Theft & Loss Prevention",
        icon: MdCategory,
        functionalities: [
          { name: "Suspicious behavior detection", isPremium: true },
          { name: "Shelf sweeping detection", isPremium: true }
        ]
      },
      {
        id: "safety-monitoring",
        title: "Safety Monitoring",
        icon: MdHealthAndSafety,
        functionalities: [
          { name: "Fall Detection", isPremium: true },
          { name: "Fight Detection", isPremium: true }
        ]
      },
      {
        id: "planogram",
        title: "Planogram Compliance",
        icon: BarChart3,
        functionalities: [
          { name: "Out-of-stock shelf detection", isPremium: false },
          { name: "Product placement verification", isPremium: false }
        ]
      },
      {
        id: "staff-deployment",
        title: "Staff Deployment",
        icon: BarChart3,
        functionalities: [
          { name: "Idle staff detection", isPremium: true },
          { name: "Customer density monitoring", isPremium: true }
        ]
      }
    ],

    "Parking Analytics": [
      {
        id: "parking-occupancy",
        title: "Parking Occupancy Monitoring",
        icon: MdLocalParking,
        functionalities: [
          { name: "Available/occupied slot detection", isPremium: false },
          { name: "Real-time parking status", isPremium: true }
        ]
      },
      {
        id: "vehicle-counting",
        title: "Vehicle Counting",
        icon: MdDirectionsCar,
        functionalities: [
          { name: "Entry/exit vehicle count", isPremium: false },
          { name: "Peak hour analysis", isPremium: true }
        ]
      },
      {
        id: "illegal-parking",
        title: "Illegal Parking Detection",
        icon: MdWarning,
        functionalities: [
          { name: "No-parking zone violation detection", isPremium: false },
          { name: "Double parking alerts", isPremium: true }
        ]
      },
      {
        id: "parking-duration",
        title: "Parking Duration Tracking",
        icon: MdAccessTime,
        functionalities: [
          { name: "Vehicle stay duration", isPremium: true },
          { name: "Overstay alerts", isPremium: true }
        ]
      }
    ]
  },

  /* =========================
     CONSTRUCTION
  ========================== */
  Construction: {
    "Construction Site Monitoring": [
      {
        id: "ppe-compliance",
        title: "PPE Compliance",
        icon: FaHardHat,
        functionalities: [
          "Helmet detection",
          "Safety boots detection",
          "Hi-vis vest detection"
        ]
      },
      {
        id: "exclusion-zone",
        title: "Exclusion Zone Monitoring",
        icon: MdCategory,
        functionalities: [
          "Crane swing zone monitoring",
          "Excavation edge detection"
        ]
      },
      {
        id: "plant-safety",
        title: "Plant & Equipment Safety",
        icon: MdFactCheck,
        functionalities: [
          "Seatbelt detection",
          "Pedestrian near equipment detection"
        ]
      },
      {
        id: "progress-monitoring",
        title: "Progress Monitoring",
        icon: BarChart3,
        functionalities: [
          "Daily site progress tracking",
          "Work completion monitoring"
        ]
      },
      {
        id: "perimeter-security",
        title: "Perimeter & After-Hours Security",
        icon: MdCategory,
        functionalities: [
          "Unauthorized entry detection",
          "Fence breach detection"
        ]
      }
    ]
  },

  /* =========================
     HOSPITALITY
  ========================== */
  Hospitality: {
    "Hotel Operations": [
      {
        id: "guest-flow",
        title: "Guest Flow & Bottlenecks",
        icon: BarChart3,
        functionalities: [
          "Lobby congestion detection",
          "Restaurant queue monitoring"
        ]
      },
      {
        id: "pool-safety",
        title: "Pool & Aquatic Safety",
        icon: MdFactCheck,
        functionalities: [
          "Distress detection",
          "Unattended children detection"
        ]
      },
      {
        id: "staff-grooming",
        title: "Staff Attendance & Grooming",
        icon: MdCategory,
        functionalities: [
          "Uniform compliance detection",
          "Staff presence tracking"
        ]
      },
      {
        id: "housekeeping",
        title: "Housekeeping Efficiency",
        icon: BarChart3,
        functionalities: [
          "Room cleaning time tracking",
          "Missed room detection"
        ]
      },
      {
        id: "security",
        title: "Security & Intruder Detection",
        icon: MdCategory,
        functionalities: [
          "Back-of-house access monitoring",
          "Intruder detection"
        ]
      }
    ]
  },
  /* =========================
     SAFETY
  ========================== */

    "Safety" : {
    "Employee Safety": [
      {
        id: "workplace-monitoring",
        title: "Workplace Monitoring",
        icon: ShieldCheck,
        functionalities: [
          "Person Detection",
          "Movement Tracking",
          "Zone Violation",
          "Fall Detection",
        ],
      },
      // {
      //   id: "emergency-response",
      //   title: "Emergency Response",
      //   icon: ShieldCheck,
      //   functionalities: [
      //     "Accident Detection",
      //     "Fire Detection",
      //     "Smoke Detection",
      //     "Panic Button",
      //   ],
      // },
    ],
    "PPE Kit": [
      {
        id: "helmet-detection",
        title: "Helmet Detection",
        icon: HardHat,
        functionalities: [
          "Hard Hat Detection",
          "Helmet Color Check",
          "Compliance Tracking",
        ],
      },
    ],
    "Phone Usage": [
      {
        id: "phone-monitoring",
        title: "Phone Monitoring",
        icon: Phone,
        functionalities: ["Phone Usage", "Restricted Zone Violation"],
      },
    ],
  },

  /* =========================
     PARKING
  ========================== */
  Parking: {
    "Parking Management": [
      {
        id: "parking-availability",
        title: "Parking Slot Availability",
        icon: BarChart3,
        functionalities: [
          "Detect occupied parking slots",
          "Detect vacant parking slots",
          "Real-time parking availability"
        ]
      },
      {
        id: "anpr-entry-exit",
        title: "ANPR Based Entry / Exit",
        icon: MdFactCheck,
        functionalities: [
          "Detect vehicle number plate",
          "Log vehicle entry time",
          "Log vehicle exit time"
        ]
      },
      {
        id: "illegal-parking",
        title: "Illegal Parking Detection",
        icon: MdCategory,
        functionalities: [
          "Detect no-parking zone violations",
          "Detect double parking",
          "Alert on illegal parking"
        ]
      },
      {
        id: "ev-parking",
        title: "EV Parking Monitoring",
        icon: BarChart3,
        functionalities: [
          "Detect EV vehicles",
          "Monitor EV charging slot usage",
          "Detect non-EV in EV slot"
        ]
      },
      {
        id: "parking-duration",
        title: "Parking Duration Analytics",
        icon: BarChart3,
        functionalities: [
          "Calculate parking duration",
          "Overstay detection",
          "Generate parking usage analytics"
        ]
      }
    ]
  },


   /* =========================
     EDUCATION
  ========================== */
  Education: {

  "Education & Campus Monitoring": [

    {
      id: "staff-gate-attendance",
      title: "Staff Attendance",
      icon: BarChart3,
       functionalities: [
    "Detect staff entry through gate",
    "Track staff exit through gate",
    "Generate staff attendance reports"
  ]
    },

    {
      id: "student-classroom-counting",
      title: "Student Classroom Counting",
      icon: BarChart3,
      functionalities: [
        "Count students entering classroom",
        "Monitor classroom occupancy",
        "Generate student attendance reports"
      ]
    },

    {
      id: "campus-safety",
      title: "Campus Safety Monitoring",
      icon: MdFactCheck,
      functionalities: [
        "Monitor common areas",
        "Detect suspicious activities",
        "Alert on safety incidents"
      ]
    },

    {
      id: "unauthorized-access",
      title: "Unauthorized Access Detection",
      icon: MdCategory,
      functionalities: [
        "Detect entry into restricted areas",
        "Detect non-student presence",
        "Alert on unauthorized access"
      ]
    }
  ]
},
 /* =========================
     WAREHOUSE
  ========================== */
  Warehouse: {
    "Warehouse & Logistics Monitoring": [
      {
        id: "forklift-human-safety",
        title: "Forklift–Human Collision Alerts",
        icon: FaHardHat,
        functionalities: [
          "Detect forklift movement",
          "Detect human presence near forklift",
          "Trigger alert on unsafe proximity"
        ]
      },
      {
        id: "package-counting",
        title: "Package Counting",
        icon: BarChart3,
        functionalities: [
          "Count packages on conveyor",
          "Count packages during loading",
          "Verify package quantity"
        ]
      },
      {
        id: "loading-unloading",
        title: "Loading / Unloading Verification",
        icon: MdFactCheck,
        functionalities: [
          "Detect loading activity",
          "Detect unloading activity",
          "Verify correct handling of goods"
        ]
      },
      {
        id: "path-optimization",
        title: "Path Optimization",
        icon: BarChart3,
        functionalities: [
          "Track forklift movement paths",
          "Analyze congestion areas"
        ]
      }
    ]
  },

  Textile: {
      "Fabric Defect Detection": [
          {
              id: "surface-defects",
              title: "Surface Defect Detection",
              icon: MdFactCheck,
              functionalities: [
              "Detect holes and tears",
              "Detect stains and oil marks",
              "Detect scratches and cuts",
              "Detect misweaves and knots"
              ]
          },
          {
              id: "pattern-anomaly",
              title: "Pattern & Texture Anomaly",
              icon: MdFactCheck,
              functionalities: [
              "Pattern mismatch detection",
              "Texture inconsistency detection",
              "Color shade variation detection",
              "Repeat pattern validation"
              ]
          }
      ],
      "Yarn & Thread Monitoring": [
          {
              id: "broken-yarn",
              title: "Broken / Missing Yarn Detection",
              icon: MdCategory,
              functionalities: [
              "Detect broken yarn",
              "Detect missing threads",
              "Detect loose threads",
              "Real-time loom alerting"
              ]
          },
          {
              id: "yarn-uniformity",
              title: "Yarn Thickness & Uniformity",
              icon: MdCategory,
              functionalities: [
              "Thickness variation detection",
              "Overlapping yarn detection",
              "Irregular spacing detection"
              ]
          }
      ],
      "Roll & Piece Measurement": [
          {
              id: "roll-counting",
              title: "Fabric Roll Counting",
              icon: BarChart3,
              functionalities: [
              "Roll counting",
              "Batch-wise roll tracking",
              "Entry / exit roll logging"
              ]
          },
          {
              id: "length-measurement",
              title: "Fabric Length Measurement",
              icon: BarChart3,
              functionalities: [
              "Continuous length estimation",
              "Cut-length validation",
              "Over / under length detection"
              ]
          }
      ],
      "Stitching & Alignment": [
          {
              id: "stitch-quality",
              title: "Stitch Quality Inspection",
              icon: CheckCircle2,
              functionalities: [
              "Broken stitch detection",
              "Uneven stitch detection",
              "Skipped stitch detection"
              ]
          },
          {
              id: "edge-alignment",
              title: "Edge & Seam Alignment",
              icon: CheckCircle2,
              functionalities: [
              "Edge alignment validation",
              "Seam straightness detection",
              "Panel mismatch detection"
              ]
          }
      ]
    },
  
  Quality: {
    "Surface Defects": [
      {
        id: "scratch",
        title: "Scratch Detection",
        icon: Scissors,
        functionalities: ["Scratch Detection"]
      },
      {
        id: "hole",
        title: "Hole Detection",
        icon: CircleDot,
        functionalities: ["Hole Detection"]
      },
      {
        id: "dent",
        title: "Dent Detection",
        icon: Circle,
        functionalities: ["Dent Detection"]
      },
      {
        id: "crack",
        title: "Crack Detection",
        icon: Activity,
        functionalities: ["Crack Detection"]
      }
    ],
    Measurement: [
      {
        id: "radius",
        title: "Radius Measurement",
        icon: Ruler,
        functionalities: ["Radius Measurement"]
      },
      {
        id: "length",
        title: "Length Measurement",
        icon: MoveHorizontal,
        functionalities: ["Length Measurement"]
      },
      {
        id: "width",
        title: "Width Measurement",
        icon: MoveVertical,
        functionalities: ["Width Measurement"]
      },
      {
        id: "depth",
        title: "Depth Measurement",
        icon: Gauge,
        functionalities: ["Depth Measurement"]
      }
    ]
  },

  "Object Counting": {
    "Person Count": [
      {
        id: "warehouse-inventory",
        title: "Warehouse Inventory",
        icon: Package,
        functionalities: [
          "Real-time person detection",
          "Stock level alerts",
          "Inventory reconciliation",
          "Automated reporting"
        ]
      },
      {
        id: "delivery-tracking",
        title: "Delivery Tracking",
        icon: Package,
        functionalities: [
          "Load verification",
          "Delivery logging",
          "Mismatch detection",
          "Route optimization"
        ]
      },
      {
        id: "safety-compliance",
        title: "Safety Compliance",
        icon: Package,
        functionalities: [
          "Regulatory reporting",
          "Overcrowding alerts",
          "Safety audit support",
          "Real-time monitoring"
        ]
      }
    ],
    "Construction Materials Counting": [
      {
        id: "material-inventory",
        title: "Material Inventory",
        icon: Boxes,
        functionalities: [
          "Bulk material counting",
          "Category-wise tracking",
          "Stock updates",
          "Automated reports"
        ]
      },
      {
        id: "site-monitoring",
        title: "Site Monitoring",
        icon: Boxes,
        functionalities: [
          "Real-time site tracking",
          "Shortage alerts",
          "Material usage logs",
          "Project progress tracking"
        ]
      },
      {
        id: "quality-control",
        title: "Quality Control",
        icon: Boxes,
        functionalities: [
          "Defect detection",
          "Count validation",
          "Supplier verification",
          "Quality audit logs"
        ]
      }
    ],
    "Vehicle & Container Monitoring": [
      {
        id: "entry-exit-tracking",
        title: "Entry / Exit Tracking",
        icon: Truck,
        functionalities: [
          "Entry/exit logging",
          "Real-time tracking",
          "Automated gate checks",
          "Historical data"
        ]
      },
      {
        id: "pallet-counting",
        title: "Pallet Counting",
        icon: Truck,
        functionalities: [
          "Pallet detection",
          "Load verification",
          "Inventory sync",
          "Discrepancy alerts"
        ]
      },
      {
        id: "fleet-management",
        title: "Fleet Management",
        icon: Truck,
        functionalities: [
          "Vehicle status tracking",
          "Maintenance alerts",
          "Route logging",
          "Fleet utilization reports"
        ]
      }
    ]
  },

  Traceability: {
      "OCR Reader": [
        {
          id: "text-extraction",
          title: "Text Extraction",
          icon: FileText,
          functionalities: [
            "Multi-language support",
            "Handwriting recognition",
            "Batch processing",
            "Real-time extraction"
          ]
        },
        {
          id: "document-analysis",
          title: "Document Analysis",
          icon: FileText,
          functionalities: [
            "Invoice parsing",
            "Form recognition",
            "Data validation",
            "Automated tagging"
          ]
        }
      ],
      "Barcode Scanner": [
        {
          id: "product-traceability",
          title: "Product Traceability",
          icon: BarChart3,
          functionalities: [
            "UPC/EAN decoding",
            "Batch tracking",
            "Inventory updates",
            "Location logging"
          ]
        }
      ],
      "QR Code Scanner": [
        {
          id: "package-validation",
          title: "Package Validation",
          icon: QrCode,
          functionalities: [
            "Version 1-40 support",
            "Error correction",
            "Protocol verification",
            "Batch scanning"
          ]
        }
      ]
  },
};

export default useCasesConfig;